import React, {useEffect, useRef} from "react";

type TvModalProps = {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    children: React.ReactNode;
    maxWidth?: string;
};

const TvModal: React.FC<TvModalProps> = ({
    open,
    onClose,
    title,
    subtitle,
    children,
    maxWidth = "720px"
}) => {
    const bodyRef = useRef<HTMLDivElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!open) return;
        previousFocusRef.current = document.activeElement as HTMLElement | null;
        const timer = setTimeout(() => {
            const autoFocusEl = bodyRef.current?.querySelector<HTMLElement>('[data-tv-autofocus="true"]');
            const firstBodyFocusable = bodyRef.current?.querySelector<HTMLElement>(
                '.tv-modal-body button:not(:disabled), .tv-modal-body input:not(:disabled), .tv-modal-body [tabindex="0"]'
            );
            const fallbackFocusable = bodyRef.current?.querySelector<HTMLElement>(
                'button:not(:disabled), input:not(:disabled), [tabindex="0"]'
            );
            (autoFocusEl || firstBodyFocusable || fallbackFocusable)?.focus({preventScroll: true});
        }, 60);
        return () => {
            clearTimeout(timer);
            const prev = previousFocusRef.current;
            if (prev && prev.isConnected) {
                setTimeout(() => prev.focus({preventScroll: true}), 30);
            }
        };
    }, [open]);

    if (!open) return null;

    return (
        <div
            className="tv-modal-overlay"
            role="dialog"
            aria-modal="true"
            onClick={e => {
                if (e.target === e.currentTarget) onClose();
            }}>
            <div className="tv-modal-dialog" style={{maxWidth}} ref={bodyRef}>
                <div className="tv-modal-header" data-tv-row="tv-modal-header">
                    <div>
                        <h3 className="tv-modal-title">{title}</h3>
                        {subtitle && <div className="tv-modal-subtitle">{subtitle}</div>}
                    </div>
                    <button
                        type="button"
                        data-tv-focusable="true"
                        data-tv-modal-close="true"
                        className="tv-btn tv-btn--ghost tv-btn--icon"
                        onClick={onClose}
                        title="Cerrar (Atrás)">
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>
                <div className="tv-modal-body" data-tv-row="tv-modal-body" data-tv-grid="true">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default TvModal;
